import React, {
  CSSProperties,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import debounce from 'lodash.debounce';

import client from '@atom/graph/client';
import { GET_USERS } from '@atom/graph/user';
import {
  Autocomplete,
  Avatar,
  Icon,
  IconButton,
  List,
  Popover,
} from '@atom/mui';
import colors from '@atom/styles/colors';
import fonts from '@atom/styles/fonts';
import {
  UserDetail,
  UsersConnection,
  UsersConnectionInput,
} from '@atom/types/user';
import { getUserFullName } from '@atom/utilities/userUtilities';

import { useChatContext } from './ChatContext';

import classes from './chat.module.css';

const { ListItemText } = List;

const DEBOUNCE_TIME = 300;
const MIN_CHAR = 3;
const SEARCH_LIMIT = 250;

const styles: Record<string, CSSProperties> = {
  icon: {
    fontSize: fonts.md,
  },
};

const ChatImpersonation = () => {
  const {
    impersonationData: {
      impersonatedUser,
      setImpersonatedUser,
      clearImpersonation,
    },
  } = useChatContext();

  const anchor = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [users, setUsers] = useState<UserDetail[]>([]);
  const [loading, setLoading] = useState(false);

  const isImpersonating = impersonatedUser !== null;
  const iconColor = isImpersonating ? colors.brand.blue : colors.neutral.gray;

  const searchUsers = async (input: UsersConnectionInput) => {
    setLoading(true);

    const { data } = await client.query<{ users: UsersConnection }>({
      query: GET_USERS,
      variables: {
        input: {
          ...input,
          limit: SEARCH_LIMIT,
          page: 1,
          showAdmin: true,
        },
      },
    });

    setUsers(data?.users?.users || []);
    setLoading(false);
  };

  const searchUsersDebounced = useCallback<
    (input: UsersConnectionInput) => void
  >(debounce(searchUsers, DEBOUNCE_TIME), []);

  useEffect(() => {
    if (query.length >= MIN_CHAR) {
      searchUsersDebounced({ name: query });
    } else {
      setUsers([]);
    }
  }, [query]);

  const handleIconClick = () => {
    if (isImpersonating) {
      clearImpersonation();
    } else {
      setOpen(true);
    }
  };

  const handleUserSelect = (
    _event: React.SyntheticEvent,
    value: UserDetail | null,
  ) => {
    if (value) {
      setImpersonatedUser(value);
      setOpen(false);
      setQuery('');
      setUsers([]);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setQuery('');
    setUsers([]);
  };

  const tooltip = isImpersonating
    ? `Impersonating: ${getUserFullName(impersonatedUser)}`
    : 'Impersonate User';

  return (
    <div ref={anchor}>
      <IconButton onClick={handleIconClick} size="small" tooltip={tooltip}>
        <Icon style={styles.icon} color={iconColor}>
          supervised_user_circle
        </Icon>
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchor.current as any}
        anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
        transformOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        onClose={handleClose}
      >
        <div className={classes.impersonationPopover}>
          <Autocomplete
            options={users}
            loading={loading}
            inputValue={query}
            onInputChange={(_event, value) => setQuery(value || '')}
            getOptionLabel={(user: UserDetail) => getUserFullName(user)}
            onChange={handleUserSelect}
            placeholder="Search users..."
            autoFocus
            renderOption={(optionProps, user: UserDetail) => (
              <li key={user.id} {...optionProps}>
                <Avatar
                  src={user.photoUrl}
                  alt={user.firstName}
                  style={{ marginRight: '0.5rem' }}
                />
                <ListItemText
                  primary={getUserFullName(user)}
                  secondary={user.email}
                />
              </li>
            )}
          />
        </div>
      </Popover>
    </div>
  );
};

export default ChatImpersonation;
